import { gql, useQuery } from '@apollo/client';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect } from 'react';
import LoadingDialog from 'src/components/dialogs/LoadingDialog';
import { meQuery } from 'src/dynamoQueries/me';
import { cleanEdgesCleanNode, cleanPayload } from 'src/helpers/common';
import { useCustomToast } from 'src/hooks/menu';
import CreateGalleryDialog from './dialogs/CreateGalleryDialog';
import CreateGalleryFromExistingCollectionDialog from './dialogs/CreateGalleryFromExistingDialog';

interface MediaGroup {
  id: string;
  name: string;
}

interface PermGroupWithMediaGroups {
  mediaGroups: {
    edges: Array<{
      node: MediaGroup;
    }>;
  };
}

interface MeQueryData {
  me: {
    pgroupMembers: {
      edges: Array<{
        node: PermGroupWithMediaGroups;
      }>;
    };
  };
}

interface CreateGallerySwitchProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId?: string | null;
  assetIds?: string[] | null;
}

export default function CreateGallerySwitch({
  isOpen,
  onClose,
  collectionId = null,
  assetIds = null,
}: CreateGallerySwitchProps): React.JSX.Element | null {
  const { toastr } = useCustomToast();

  const buildQuery = () => {
    const q = meQuery(true);
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  const { loading, data, error } = useQuery<MeQueryData>(buildQuery(), {});

  useEffect(() => {
    if (error) {
      toastr.error(error.message);
    }
  }, [error, toastr]);

  if (loading) {
    return <LoadingDialog header={'Creating a gallery ...'} />;
  }

  const gatherMediaGroups = (): MediaGroup[] => {
    const permGroups = cleanPayload(data?.me) as PermGroupWithMediaGroups[] | null;

    if (!permGroups) {
      return [];
    }

    const mediaGroups = permGroups.map((permGroup) => permGroup.mediaGroups);
    const cleanedMGs = mediaGroups.map((mg) => cleanEdgesCleanNode(mg) as MediaGroup[]);
    const cleanDups = cleanedMGs.flat().filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);

    // flatten mediaGroups
    return cleanDups
      ? cleanDups.sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        })
      : [];
  };

  if (collectionId)
    return (
      <CreateGalleryFromExistingCollectionDialog
        isOpen={isOpen}
        onClose={onClose}
        mediaGroups={gatherMediaGroups()}
        collectionId={collectionId}
      />
    );
  if (assetIds)
    return (
      <CreateGalleryDialog isOpen={isOpen} onClose={onClose} assetIds={assetIds} mediaGroups={gatherMediaGroups()} />
    );

  return null;
}
